import type { CSSProperties, ReactNode } from "react";
import sprite from "../../assets/icons.svg?raw";
import { t } from "../../i18n";

/** The icon sprite from the mockup, rendered once at the root. */
export function IconSprite() {
  // A static, bundled SVG asset (no user data), injected once. Not display:none: Safari won't
  // render <use> references into a hidden subtree; the sprite itself is 0x0.
  return <div className="sprite" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sprite }} />;
}

export function Icon({ name, size, label }: { name: string; size?: "s" | "l"; label?: string }) {
  return (
    <svg
      className={size ? `ic ${size}` : "ic"}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
    >
      <use href={`#${name}`} />
    </svg>
  );
}

/** The short mark (the V and its viewer) on the orange tile: the rail, the agent's badges. The
 *  full mark (Focus) crowds below 40 px, so these small places keep the short one. */
export function Logo({ small }: { small?: boolean }) {
  return (
    <span className={small ? "logo sm" : "logo"}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <use href="#vmark" />
      </svg>
    </span>
  );
}

/** The full mark (Focus: the V and its viewer in a viewfinder), bare and orange, for the big
 *  places (the owner's pick, 2026-10-08: design/logo-variants.html, B). */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg
      className={className ? `brand-mark ${className}` : "brand-mark"}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <use href="#vmark-focus" />
    </svg>
  );
}

/** The lockup: the full mark and VANTAGE EX in spaced capitals (the EX in orange). The text
 *  stays "Vantage EX" for screen readers and search; the capitals are CSS. */
export function BrandLockup() {
  return (
    <span className="brand-lockup">
      <BrandMark />
      <span className="bl-name">
        Vantage <span className="bl-ex">EX</span>
      </span>
    </span>
  );
}

/** A stable hue (0-359) for people without one set. */
export function hueFor(id: string, hue?: number | null): number {
  if (hue !== null && hue !== undefined) return hue;
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

type AvatarProps = { id: string; name: string; hue?: number | null; size?: "xs" | "sm" | "lg" };

export function Avatar({ id, name, hue, size }: AvatarProps) {
  const style = { "--h": hueFor(id, hue) } as CSSProperties;
  return (
    <span className={size ? `av ${size}` : "av"} style={style} aria-hidden="true">
      {Array.from(name.trim())[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

/** An avatar with the presence dot (the mockup's .pres: online, .away, .off). */
export function PresenceAvatar({
  id,
  name,
  hue,
  status,
  size = "xs",
}: AvatarProps & { status: "online" | "away" | "offline" }) {
  const style = { "--h": hueFor(id, hue) } as CSSProperties;
  const cls = status === "online" ? "pres" : status === "away" ? "pres away" : "pres off";
  const label =
    status === "online"
      ? t("shell.isOnline", { name })
      : status === "away"
        ? t("shell.isAway", { name })
        : t("shell.isOffline", { name });
  return (
    <span className={size ? `av ${size}` : "av"} style={style} data-presence={status}>
      <span aria-hidden="true">{Array.from(name.trim())[0]?.toUpperCase() ?? "?"}</span>
      <span className={cls} role="img" aria-label={label} />
    </span>
  );
}

/** R7.5 contract 6: the channel icons a team can pick (the backend's TEAM_ICONS, in order). */
export const TEAM_ICONS = [
  "code",
  "terminal",
  "rocket",
  "flask",
  "wrench",
  "shield",
  "chart",
  "palette",
  "camera",
  "heart",
  "briefcase",
  "users",
  "megaphone",
  "cart",
  "globe",
  "book",
  "star",
  "coffee",
] as const;
export type TeamIconName = (typeof TEAM_ICONS)[number];

/** The colours a team can pick (the backend's TEAM_HUES), with their names. Each name is read
 *  when it's used (a getter), so it follows the interface language. */
export const TEAM_HUES = [
  {
    token: "team-dev",
    get label() {
      return t("shell.hueBlue");
    },
  },
  {
    token: "team-qa",
    get label() {
      return t("shell.hueTeal");
    },
  },
  {
    token: "team-violet",
    get label() {
      return t("shell.hueViolet");
    },
  },
  {
    token: "team-hr",
    get label() {
      return t("shell.huePink");
    },
  },
  {
    token: "team-amber",
    get label() {
      return t("shell.hueAmber");
    },
  },
  {
    token: "team-green",
    get label() {
      return t("shell.hueGreen");
    },
  },
  {
    token: "team-all",
    get label() {
      return t("shell.hueGrey");
    },
  },
] as const;
export type TeamHue = (typeof TEAM_HUES)[number]["token"];

/** What a team looks like: its icon and its colour token. */
export type TeamLook = { icon?: string | null; hue_token?: string | null };

function iconOf(team: TeamLook): TeamIconName {
  return (TEAM_ICONS as readonly string[]).includes(team.icon ?? "")
    ? (team.icon as TeamIconName)
    : "users";
}

/** A team's channel icon in the team's colour (R7.5 contract 6: it replaces the colour square
 *  everywhere a team shows). No team is the company: a building in grey. */
export function TeamIcon({
  team,
  size,
  label,
}: {
  team?: TeamLook | null;
  size?: "s" | "l";
  label?: string;
}) {
  const icon = team ? iconOf(team) : null;
  const color = team ? `var(--${team.hue_token || "team-all"})` : "var(--fg-3)";
  return (
    <svg
      className={size ? `ic ticon ${size}` : "ic ticon"}
      style={{ color }}
      data-icon={icon ?? "building"}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
    >
      <use href={icon ? `#t-${icon}` : "#i-building"} />
    </svg>
  );
}

/** A team's name with its icon (People, Memory, Automations' owners); no team: the company. */
export function TeamChip({ team, children }: { team?: TeamLook | null; children: ReactNode }) {
  return (
    <span className="tchip">
      <TeamIcon team={team} size="s" />
      {children}
    </span>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="toggle"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

/** Single-choice segment (the mockup's .seg2 / .lvl), arrow keys move the choice. */
export function Choice<T extends string>({
  value,
  options,
  onChange,
  label,
  className = "seg2",
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  const move = (dir: 1 | -1) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + dir + options.length) % options.length];
    if (next) onChange(next.value);
  };
  return (
    <div
      className={className}
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          move(1);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          data-l={o.value}
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The access levels; each label is read when it's used (a getter), in the interface language. */
export const LEVEL_OPTIONS = [
  {
    value: "none" as const,
    get label() {
      return t("shell.levelNone");
    },
  },
  {
    value: "read" as const,
    get label() {
      return t("shell.levelRead");
    },
  },
  {
    value: "write" as const,
    get label() {
      return t("shell.levelWrite");
    },
  },
  {
    value: "manage" as const,
    get label() {
      return t("shell.levelManage");
    },
  },
];

export function StatusDot({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "danger" | "muted";
  children: ReactNode;
}) {
  return <span className={`statusx ${tone}`}>{children}</span>;
}
